import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import Button from '~/components/common/Button'
import Input from '~/components/common/Input'
import Select from '~/components/common/Select'
import ModalPhone from '~/components/print/ModalPhone'
import ModalSuratKuasaVerify from '~/components/print/ModalSuratKuasaVerify'
import { recaptchaSiteKey } from '~/configs/app'
import { ERROR_MESSAGES } from '~/constants/messages'
import useHandleError from '~/hooks/useHandleError'
import PrintLayout from '~/layouts/PrintLayout'
import counter from '~/services/counter'
import pickup from '~/services/pickup'
import type { OptionProps } from '~/types/component'
import type { ValidateCodeResData } from '~/types/pickup'
import { transformBranches, transformNeeds } from '~/utils/dataTransformers'

// jba-web-api always generates an 8-digit kode_unik (str_pad(random_int(0, 99999999), 8)) —
// once the customer's typed that many characters, the code is complete enough to look up.
const KODE_UNIK_LENGTH = 8

const Print = () => {
  const handleError = useHandleError()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm()
  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const [isSuratKuasaModalOpen, setSuratKuasaModalOpen] = useState<boolean>(false)
  const [pickupData, setPickupData] = useState<ValidateCodeResData['data'] | null>(null)
  const { branch } = useParams()

  const selectedBranch = watch('branch') as OptionProps | undefined
  const selectedNeed = watch('need') as OptionProps | undefined
  const kodeUnikValue = watch('kode_unik') as string | undefined

  // Explicit submit ("Teruskan") opens the phone/kuasa modal; the keyup
  // auto-lookup below only fills in cabang+keperluan so the customer can see
  // it worked, without jumping into the modal before they've pressed anything.
  const openModalOnSuccessRef = useRef(true)

  useEffect(() => {
    register('branch', { required: ERROR_MESSAGES.REQUIRED_COUNTER })
    register('need', { required: ERROR_MESSAGES.REQUIRED_NEED })
    register('kode_unik')
  }, [register])

  useEffect(() => {
    if (selectedBranch) {
      setValue('need', undefined)
    }
  }, [selectedBranch, setValue])

  const branchQuery = useQuery({
    queryKey: ['branches'],
    queryFn: counter.getBranches,
    select: transformBranches,
    onError: handleError,
  })

  const counterQuery = useQuery({
    queryKey: ['counters', selectedBranch?.value],
    queryFn: () => counter.getCounters(selectedBranch?.value as number),
    enabled: !!selectedBranch?.value,
    select: transformNeeds,
    onError: handleError,
  })

  // Runs after the branch auto-select below (which resets 'need') and after
  // counterQuery re-fetches for the newly-selected branch — matches the
  // kode_unik's resolved necessary_id against that branch's Keperluan options.
  useEffect(() => {
    if (pickupData?.antrian_necessary_id && counterQuery.data) {
      const needFound = counterQuery.data.find((item) => item.value === pickupData.antrian_necessary_id)
      if (needFound) {
        setValue('need', needFound)
      }
    }
  }, [pickupData, counterQuery.data, setValue])

  // Kode Unik (from the jba-web pickup-schedule flow) is optional — filling it in
  // skips the manual cabang/keperluan pick and pulls those straight from the code.
  const validateCodeMutation = useMutation({
    mutationFn: pickup.validateCode,
    onError: handleError,
    onSuccess: (res) => {
      setPickupData(res.data)
      // Visually reflect what the backend already resolved, so the kiosk screen
      // doesn't show an empty/stale cabang+keperluan while a code-based pickup
      // proceeds under the hood. Keperluan itself is auto-selected by the
      // effect below once counterQuery.data for this branch has loaded.
      if (res.data.antrian_location_id && branchQuery.data) {
        const branchFound = branchQuery.data.find((item) => item.value === res.data.antrian_location_id)
        if (branchFound) {
          setValue('branch', branchFound)
        }
      }
      if (!openModalOnSuccessRef.current) {
        return
      }
      // Dikuasakan (Surat Kuasa) goes through its own self-contained modal —
      // verify penerima kuasa's KTP, then one combined signature+consent+phone
      // step gets the queue ticket directly. Self-pickup and batch codes still
      // go through the plain phone-only ModalPhone below.
      if (res.data.type === 'surat_kuasa') {
        setSuratKuasaModalOpen(true)
      } else {
        setModalOpen(true)
      }
    },
  })

  // Auto-lookup as soon as the customer finishes typing the 8-digit kode_unik,
  // so cabang+keperluan visibly fill in before they even reach for "Teruskan".
  useEffect(() => {
    const trimmed = kodeUnikValue?.trim()
    if (trimmed?.length === KODE_UNIK_LENGTH && !validateCodeMutation.isLoading) {
      openModalOnSuccessRef.current = false
      validateCodeMutation.mutate({ kode_unik: trimmed.toUpperCase() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kodeUnikValue])

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const kodeUnik = (data.kode_unik as string | undefined)?.trim()
    if (kodeUnik) {
      openModalOnSuccessRef.current = true
      validateCodeMutation.mutate({ kode_unik: kodeUnik.toUpperCase() })
      return
    }
    if (data.branch && data.need) {
      setModalOpen(true)
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setPickupData(null)
    setValue('kode_unik', '')
  }

  const handleCloseSuratKuasaModal = () => {
    setSuratKuasaModalOpen(false)
    setPickupData(null)
    setValue('kode_unik', '')
  }

  useEffect(() => {
    if (branch && branchQuery.data) {
      const branchFound = branchQuery.data?.find((item) => item.label.toLowerCase() === branch)
      if (branchFound) {
        setValue('branch', branchFound)
      }
    }
  }, [branch, branchQuery.data, setValue])

  return (
    <PrintLayout>
      <div className="w-full max-w-6xl px-4 pt-4 md:pt-0 xl:px-0">
        <img src="/jba-logo.svg" alt="JBA Logo" className="mx-auto mb-4 h-16 sm:mb-6 sm:h-20 md:ml-6" />
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="relative rounded-3xl bg-jba-blue p-6 text-white md:p-12 lg:pr-24 xl:pr-48">
              <h1 className="mb-2 text-center text-2xl font-bold sm:text-left sm:text-4xl">Selamat Datang</h1>
              <div className="mb-4 text-center text-sm sm:mb-10 sm:text-left sm:text-base lg:text-2xl">
                Layanan pemesanan tiket layanan customer service JBA Indonesia
              </div>
              <div className="mb-4 grid max-w-xl gap-4 sm:mb-10 sm:gap-6 md:max-w-2xl md:grid-cols-[2fr_1fr_2fr] lg:max-w-xl lg:grid-cols-[2fr_1fr_2fr] xl:max-w-2xl">
                <div>
                  <label className="mb-2 inline-block rounded-xl bg-jba-lightBlue px-2 py-1 text-sm md:text-xs lg:text-sm">
                    Cabang JBA yang dikunjungi
                  </label>
                  <Select
                    placeholder="Pilih cabang"
                    icon="mdi:map-marker-radius"
                    rounded="xl"
                    font="lg"
                    options={branchQuery.data ?? []}
                    isLoading={branchQuery.isLoading}
                    isLocation
                    selected={selectedBranch}
                    setSelected={(value) => {
                      setValue('branch', value)
                      clearErrors('branch')
                    }}
                    disabled={branchQuery.isLoading}
                    errorMessage={errors.branch?.message as string}
                  />
                </div>

                <div>
                  <label className="mb-2 inline-block rounded-xl bg-jba-lightBlue px-2 py-1 text-sm md:text-xs lg:text-sm">
                    Kode Unik
                  </label>
                  <Input type="text" rounded="xl" icon="mdi:qrcode" placeholder="jika ada" {...register('kode_unik')} />
                </div>

                <div>
                  <label className="mb-2 inline-block rounded-xl bg-jba-lightBlue px-2 py-1 text-sm md:text-xs lg:text-sm">
                    Pilih keperluan
                  </label>
                  <Select
                    placeholder="Pilih keperluan"
                    icon="fluent:ticket-20-filled"
                    rounded="xl"
                    font="lg"
                    options={counterQuery.data ?? []}
                    isLoading={selectedBranch && counterQuery.isLoading}
                    selected={selectedNeed}
                    setSelected={(value) => {
                      setValue('need', value)
                      clearErrors('need')
                    }}
                    disabled={!selectedBranch || counterQuery.isLoading}
                    errorMessage={errors.need?.message as string}
                  />
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-3">
                <Button
                  type="submit"
                  variant="secondary"
                  rounded="xl"
                  font="lg"
                  isLoading={validateCodeMutation.isLoading}
                >
                  {validateCodeMutation.isLoading ? 'Memeriksa kode...' : 'Teruskan'}
                </Button>
              </div>
              <img
                src="/cs.png"
                className="fixed inset-x-0 bottom-0 -z-10 mx-auto hidden w-56 sm:w-64 md:absolute md:left-auto md:right-2 md:z-10 md:mx-0 md:inline-block md:w-72 lg:right-5 lg:w-96 xl:right-10"
              />
            </div>
          </form>
          <div className="pl-4 pt-4 text-center text-xs text-gray-500">
            Copyright © {new Date().getFullYear()} JBA Antrean System. All rights reserved.
          </div>

          <ModalSuratKuasaVerify
            isOpen={isSuratKuasaModalOpen}
            closeModal={handleCloseSuratKuasaModal}
            kodeUnik={pickupData?.kode_unik || ''}
            locationId={pickupData?.antrian_location_id || ''}
            necessaryId={pickupData?.antrian_necessary_id || ''}
            prefillPhone={pickupData?.customer_phone || undefined}
            onDone={handleCloseSuratKuasaModal}
          />

          <ModalPhone
            isOpen={isModalOpen}
            closeModal={handleCloseModal}
            locationId={pickupData?.antrian_location_id || selectedBranch?.value.toString() || ''}
            locationName={pickupData?.pickup_location_name || selectedBranch?.label || ''}
            necessaryId={pickupData?.antrian_necessary_id || selectedNeed?.value.toString() || ''}
            kodeUnik={pickupData?.kode_unik}
            prefillPhone={pickupData?.customer_phone || undefined}
          />
        </GoogleReCaptchaProvider>
      </div>
    </PrintLayout>
  )
}

export default Print
